import { useState, useEffect, useRef } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { supabase } from "../../supabaseClient.js";
import useAlert from '../../alert/useAlert.js';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import { sub } from 'date-fns';
import { toStorageKeySegment } from '../../utils/storageKey.js';

export const useKnowledgeBaseData = () => {
    const [categories, setCategories] = useState([]);
    const [newCategory, setNewCategory] = useState(false);
    const [newDocument, setNewDocument] = useState(false);
    const [categoryTitle, setCategoryTitle] = useState("");
    const [categoryDescription, setCategoryDescription] = useState("");
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [categoryID, setCategoryID] = useState("");
    const [files, setFiles] = useState([]);
    const [viewFiles, setViewFiles] = useState([]);
    const [isViewingFiles, setIsViewingFiles] = useState(false);
    const [loading, setLoading] = useState(false);
    const [subscriptionActiveStatus, setSubscriptionActiveStatus] = useState(null);

    const { userDetails } = useSelector((state) => ({userDetails: state.globalState.userDetails}), shallowEqual);
    const currentOrg = useSelector((state) => state.globalState.currentOrg, shallowEqual);
    const { setAlert } = useAlert();
    const hasMounted = useRef(false);

    useEffect(() => {
        if (hasMounted.current) { return; }
        fetchSubscriptionInfo();
        fetchCategories();
        hasMounted.current = true;
    }, []);

    const handleFileUpload = async (event) => {
        const files = Array.from(event.target.files);
        setFiles((prevFiles) => [...prevFiles, ...files]);  
    };
  
    const handleDrop = (event) => {
        event.preventDefault();
        const files = event.dataTransfer.files;
        handleFileUpload({ target: { files } });
    };
  
    const handleDownload = (file) => {
        const fileData = new Blob([file], { type: "text/plain" });
        const url = window.URL.createObjectURL(fileData);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', file.name);
        document.body.appendChild(link);
        link.click();
    };
  
    const handleDelete = (index) => {
        setFiles((prevFiles) => {
            const updatedFiles = prevFiles.filter((_, i) => i !== index);
            return updatedFiles;
        });
    };

    const fetchSubscriptionInfo = async () => {
        try {
        if (!userDetails || !userDetails.id) return;
        
        const { data, error } = await supabase.from('subscription_info').select('is_active').eq('user_id', userDetails.id).single();
            
        if (error && error.code !== 'PGRST116') {
            console.error('Error fetching subscription info:', error);
            return;
        }
        if(!data) {
            setSubscriptionActiveStatus(false);
            return;
        }
        if (data) {
            setSubscriptionActiveStatus(data.is_active);
        }
        } catch (error) {
        console.error('Error in fetchSubscriptionInfo:', error);
        }
    };

    const fetchCategories = async () => {
        try {
            let { data, error } = await supabase.from('knowledgebase').select('*').eq('teamid', currentOrg ? currentOrg.id : '');
            if(error){
                console.error(error);
                return;
            }
            const sortedCategories = [...data].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
            setCategories(sortedCategories);
        } catch (error) {
            console.error(error);
        }
    };

    const handleAddCategory = async (title, description) => {
        let { error } = await supabase.from('knowledgebase').insert([{ teamid: currentOrg ? currentOrg.id : null, title, description, documents: [] }]);
        if(error){
            setAlert({ message: 'Failed to add the new category!', severity: "error" });
            return;
        }
        await fetchCategories();
    };

    // function hashFileName(fileName) {
    //     return CryptoJS.SHA256(fileName).toString(CryptoJS.enc.Hex);
    // }

    // Storage keys must be ASCII-safe; both the category title and the file name are
    // user input. The DB keeps the original names, so keys are rebuilt from them here.
    const kbObjectPath = (categoryTitle, fileName) =>
        `${currentOrg.id}/${toStorageKeySegment(categoryTitle)}/${toStorageKeySegment(fileName)}`;

    const handleAddDocumentsSupabase = async (categoryTitle, files) => {
        for (const file of files) {
            const { error } = await supabase.storage.from('tikcare-attachments').upload(kbObjectPath(categoryTitle, file.name), file, {
                cacheControl: '3600',
                upsert: true
            })
            if (error) {
                console.error('Error uploading file:', error);
                if (error.message && error.message.startsWith('Invalid key')) {
                    throw new Error(`Upload failed: "${file.name}" contains unsupported characters. Please rename the file and try again.`);
                }
                throw new Error('Error uploading file!');
            }
        }
    }

    const handleDeleteDocumentsSupabase = async (categoryTitle, fileName) => {
        const { error } = await supabase.storage.from('tikcare-attachments').remove([kbObjectPath(categoryTitle, fileName)]);
        if (error) {
            console.error('Error deleting file:', error);
            throw new Error('Error deleting file:', error);
        }
    }

    
    const downloadFile = async (categoryID, fileName) => {
        const categoryTitle = categories.find(category => category.id === categoryID).title;
        const { data, error } = await supabase.storage.from('tikcare-attachments').download(kbObjectPath(categoryTitle, fileName));
        if (error) {
            console.error('Error downloading file:', error);
            setAlert('Failed to download the file!', 'error');
        } 
        else {
            const url = window.URL.createObjectURL(data);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
        }
    }

    const handleAddDocuments = async (categoryID, files) => {
        const formData = new FormData();
        Array.from(files).forEach((file) => {
          formData.append('files', file);
        });
        const categoryTitle = categories.find(category => category.id === categoryID).title;
        formData.append('knowledgebase_id', categoryID);
        formData.append('team_id', currentOrg ? currentOrg.id : '');
        try {
            axios.post(`${process.env.REACT_APP_RAG_BACKEND_URL}/upload_knowledgebase_documents`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Access-Control-Allow-Origin': '*',
                },
            });
            await handleAddDocumentsSupabase(categoryTitle, files);
            const newFileNames = files.map(file => file.name);
            const fileNames = categories.find(category => category.id === categoryID).documents.concat(newFileNames);
            const dateTime = new Date().toISOString();
            let { error } = await supabase.from('knowledgebase').update({ documents: fileNames, updatedAt: dateTime}).eq('id', categoryID);
            if(error){
                setAlert('Failed to add the new documents!', 'error');
                return;
            }
            setAlert('Document added successfully.', 'success');
            await fetchCategories();
        } catch (error) {
            console.error('Error uploading files:', error);
            if (error.response && error.response.data && error.response.data.error) {
                setAlert(`${error.response.data.error}`, 'error');
            } else {
                setAlert(error.message || 'Failed to upload the new documents!', 'error');
            }
        }
        setLoading(false);
    };

    const handleDeleteDocument = async (categoryID, fileName) => {
        const categoryTitle = categories.find(category => category.id === categoryID).title;
        const data = {
            knowledgebase_id: categoryID,
            fileName: fileName,
        };
        try {
            axios.post(`${process.env.REACT_APP_RAG_BACKEND_URL}/delete_knowledgebase_documents`, data, {
                headers: {
                    'Content-Type': 'application/json',
                    'Access-Control-Allow-Origin': '*'
                },
            });
            await handleDeleteDocumentsSupabase(categoryTitle, fileName);
            const fileNames = categories.find(category => category.id === categoryID).documents.filter(document => document !== fileName);
            const dateTime = new Date().toISOString();
            let { error } = await supabase.from('knowledgebase').update({ documents: fileNames, updatedAt: dateTime }).eq('id', categoryID);
            if(error){
                setAlert('Failed to delete the document!', 'error');
                return;
            }
            await fetchCategories();
            if(fileNames.length === 0){
                setIsViewingFiles(false);
            }
            setViewFiles(fileNames);
            setAlert('Document deleted successfully.', 'success');
        }
        catch (error) {
            console.error('Error deleting file:', error);
            setAlert('Failed to delete the document!', 'error');
        }
        setLoading(false);
    }

    const handleDeleteCategory = async (categoryID) => {
        try {
            const categoryToDelete = categories.find(category => category.id === categoryID);
            if (!categoryToDelete) {
                setAlert('Category not found!', 'error');
                return;
            }

            const categoryTitle = categoryToDelete.title;
            
            // Delete all documents from Supabase storage
            if (categoryToDelete.documents && categoryToDelete.documents.length > 0) {
                const filePaths = categoryToDelete.documents.map(fileName => kbObjectPath(categoryTitle, fileName));
                
                const { error: storageError } = await supabase.storage
                    .from('tikcare-attachments')
                    .remove(filePaths);
                
                if (storageError) {
                    console.error('Error deleting files from storage:', storageError);
                    setAlert('Failed to delete documents from storage!', 'error');
                    return;
                }
            }

            // Delete from RAG backend if there are documents
            // if (categoryToDelete.documents && categoryToDelete.documents.length > 0) {
            //     try {
            //         await axios.post(`${process.env.REACT_APP_RAG_BACKEND_URL}/delete_knowledgebase_category`, {
            //             knowledgebase_id: categoryID,
            //             team_id: currentOrg ? currentOrg.id : ''
            //         }, {
            //             headers: {
            //                 'Content-Type': 'application/json',
            //                 'Access-Control-Allow-Origin': '*'
            //             },
            //         });
            //     } catch (ragError) {
            //         console.error('Error deleting from RAG backend:', ragError);
            //         // Continue with database deletion even if RAG fails
            //     }
            // }

            // Delete the category from the database
            const { error } = await supabase
                .from('knowledgebase')
                .delete()
                .eq('id', categoryID);

            if (error) {
                console.error('Error deleting category:', error);
                setAlert('Failed to delete the category!', 'error');
                return;
            }

            setAlert('Category deleted successfully.', 'success');
            await fetchCategories();
            
        } catch (error) {
            console.error('Error in handleDeleteCategory:', error);
            setAlert('Failed to delete the category!', 'error');
        }
    };


    return { 
        categories, 
        setCategories, 
        handleAddCategory, 
        handleAddDocuments, 
        setAlert, 
        handleDeleteDocument,
        newCategory,
        setNewCategory,
        newDocument,
        setNewDocument,
        categoryTitle,
        setCategoryTitle,
        categoryDescription,
        setCategoryDescription,
        formSubmitted,
        setFormSubmitted,
        categoryID,
        setCategoryID,
        files,
        setFiles,
        viewFiles,
        setViewFiles,
        isViewingFiles,
        setIsViewingFiles,
        handleFileUpload,
        handleDrop,
        handleDownload,
        handleDelete,
        loading,
        setLoading,
        downloadFile,
        subscriptionActiveStatus,
        handleDeleteCategory,
        userDetails
    };

}

  